import React from 'react';
import classNames from 'classnames';
import { Icon } from '../Icon/Icon';
import { Button } from '../Button/Button';
/**
 * Bronson [Instruction Stepper](https://bronson.vwfs.io/bluelabel/components/detail/bronson-instruction-stepper.html) component.
 */
export function InstructionStepper({ center, children, className, displayIcons = true, numbered, small, statusActiveText, statusNotActiveText, reversed, testId, vertical, ...otherProps }) {
    const olClassNameList = classNames('c-instruction-stepper ', {
        'c-instruction-stepper--numbered ': numbered, // Convenience prop from Bronson variants.
        'c-instruction-stepper--vertical ': vertical, // Convenience prop from Bronson variants.
        'c-instruction-stepper--center ': center, // Convenience prop from Bronson variants.
        'c-instruction-stepper--small ': small, // Convenience prop from Bronson variants.
        'c-instruction-stepper--reversed ': reversed, // Convenience prop from Bronson variants.
    }, className).trim();
    /**
     * Determine whether this stepper should display icons.
     * The `numbered` prop overrules the `displayIcons` prop to avoid number/icon collisions.
     * @type {boolean}
     */
    const displayIcon = !numbered && displayIcons;
    return (React.createElement("ol", { ...otherProps, "data-testid": testId, className: olClassNameList, role: "list" }, React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
            return React.cloneElement(child, {
                displayIcon,
                statusActiveText,
                statusNotActiveText,
            });
        }
    }) /* Use {@link InstructionStepperStep} component. */));
}
InstructionStepper.displayName = 'InstructionStepper';
/**
 * Bronson InstructionStepperStep component (nested).
 * @internal
 * @constructor
 */
export function InstructionStepperStep({ buttonHref, buttonLabel, buttonProps, children, displayIcon, icon, state, statusActiveText, statusNotActiveText, title, ...otherProps }) {
    const liClassNameList = classNames('c-instruction-stepper__step ', { [`${state} `]: state }).trim();
    function renderIfDisplayIcons() {
        if (displayIcon) {
            return React.createElement(Icon, { name: icon });
        }
        return null;
    }
    function renderIfDescription() {
        if (children) {
            return React.createElement("div", { className: "c-instruction-stepper__description" }, children);
        }
        return null;
    }
    function renderIfButton() {
        if (buttonLabel) {
            return (React.createElement(Button, { className: "c-btn--small c-btn--link", icon: "semantic-forward", element: "a", href: buttonHref, ...buttonProps }, buttonLabel));
        }
        return null;
    }
    return (React.createElement("li", { ...otherProps, className: liClassNameList },
        React.createElement("div", { className: "c-instruction-stepper__item", "aria-hidden": "true" }, renderIfDisplayIcons()),
        React.createElement("div", { className: "c-instruction-stepper__detail" },
            React.createElement("div", { className: "c-instruction-stepper__title" }, title),
            React.createElement("div", { className: "u-visually-hidden" }, state === 'is-active' ? statusActiveText : statusNotActiveText),
            renderIfDescription(),
            renderIfButton())));
}
InstructionStepperStep.displayName = 'InstructionStepper.Step';
InstructionStepper.Step = InstructionStepperStep;
